import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import columnAliasService from '@api/v1/columnAliasService'
import { COLUMN_ALIASES } from '@config/featureFlags'
import useReadState from '@hooks/useReadState'

/**
 * Per-firm column aliases for one report (spec 061).
 *
 * Shared cache key, so the report page, the inline editor and the Settings
 * editor all read the same aliases and a rename in one is visible in the others
 * without a refetch. Jordan chose BOTH entry points (O3, 2026-08-11), which
 * makes a single source of truth load-bearing rather than tidy: two editors
 * with two caches is how the same picker drifted into two implementations
 * before (ObjectLookup / LookupFilter).
 *
 * `reportKey` is the report id for generic reports, or the page's `actionKey`
 * for the 21 specialized ones.
 */
export const columnAliasKey = (reportKey) => ['report-column-aliases', String(reportKey ?? '')]

const EMPTY = { aliases: {}, meta: {}, canManage: false, permissionResolved: false, storageConfigured: false }

export const useColumnAliases = (reportKey) => {
  const enabled = COLUMN_ALIASES && reportKey != null && reportKey !== ''

  const query = useQuery({
    queryKey: columnAliasKey(reportKey),
    queryFn: () => columnAliasService.get(reportKey),
    enabled,
    // Aliases change rarely and are read on every report open.
    staleTime: 5 * 60_000,
    // The service already resolves rather than rejects, so a retry buys
    // nothing but latency on a report that renders fine without it.
    retry: false,
  })
  const { data, refetch } = query

  /*
   * A read has THREE outcomes, not two (CC-53132, `@api/v1/readState`).
   *
   * `loading` used to be react-query's `isLoading`, i.e. `isPending && isFetching`
   * — FALSE for a query that is pending but never STARTED. That is the paused
   * case: with the default `networkMode: 'online'` react-query will not fire while
   * `onlineManager` believes the browser is offline, so `queryFn` is never called
   * and `data` stays undefined. The destructuring default then handed every
   * consumer `EMPTY` and a finished-looking `loading: false` — "this report has no
   * renamed columns", as a settled answer, about a question nobody asked.
   *
   * That matters more here than the empty grid it resembles: the firm's own words
   * are what disappear. A money column reverts to Centerbase's label on all ~119
   * generic reports and the 21 specialized pages with nothing on screen saying so,
   * which is the failure mode of "Column aliases had two doors, one wired" all
   * over again. `state` is what lets the injection point (`ReportView`) and the
   * Rename-columns editor say it out loud, and `refetch` is what gives that
   * sentence a Try again to attach to — today the nearest copy says "Try again
   * shortly" with nothing to click.
   *
   * NOTE this query can never reach `'failed'`: `columnAliasService.get` catches
   * and resolves to the refusing shape by design (a report showing CB's labels is
   * correct, just not customised), so a genuinely failed read arrives here as
   * `ready` with no aliases. That trade predates this fix and lives in the
   * service; `state` only recovers the reads that never happened.
   */
  const state = useReadState(`column-aliases:${reportKey}`, query)

  // Flag off ⇒ behave exactly as before the feature existed: CB's labels, no
  // editor anywhere. Not merely "no aliases" — `canManage` must stay false too,
  // or the affordance appears with nothing behind it. The branch stays explicit
  // rather than leaning on the disabled query: a sibling mount can leave real
  // data in this cache key, and a disabled read must not serve it.
  //
  // `loading` matters to the editor specifically: `canManage` is false until the
  // read lands, so an editor that reads it naively flashes "we could not
  // confirm your permissions" at an admin for one frame before enabling. That
  // is a false denial, and the fields are disabled underneath it. It now means
  // ONLY in-flight — a paused read is `blocked`, not loading, because "Checking
  // your permissions…" forever is its own kind of lie.
  //
  // `data` keeps falling back to `EMPTY` on an unresolved read: `aliases` is
  // dereferenced as a map (`aliases[columnId]`) in the editor and in
  // `applyColumnAliases`, so `undefined` would throw mid-render rather than
  // surface anything. `ready` is the signal; the shape is just kept safe.
  return useMemo(
    () => (enabled
      ? { ...(data ?? EMPTY), state, ready: state === 'ready', loading: state === 'loading', refetch }
      : { ...EMPTY, state: 'idle', ready: false, loading: false, refetch }),
    [enabled, data, state, refetch]
  )
}

/** Set / reset one column, and reset the whole report. Admin paths. */
export const useColumnAliasMutations = (reportKey) => {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: columnAliasKey(reportKey) })

  const setAlias = useMutation({
    mutationFn: ({ columnId, alias }) => columnAliasService.set(reportKey, columnId, alias),
    onSuccess: invalidate,
  })

  const resetAll = useMutation({
    mutationFn: () => columnAliasService.resetAll(reportKey),
    onSuccess: invalidate,
  })

  return { setAlias, resetAll }
}

export default useColumnAliases
